// One truck's page: a dark header with its photo, today's stop (when it has
// one), the stops still to come this month, and a month calendar. Choosing a
// stop day on the calendar scrolls to that stop and highlights it.
//
// Today's card always comes from the current month, so it stays put while the
// customer looks ahead at another month.
import React, { useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Box, CircularProgress } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DirectionsOutlinedIcon from "@mui/icons-material/DirectionsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { truckSchedule } from "../../apollo/server";
import { resizedImage } from "../../utils/resizedImage";
import { shortAddress, titleCaseSpot } from "../../utils/truckText";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS, TYPE, topBarOffset } from "../../utils/designTokens";
import RmfDialog, { DialogActions, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";
import { localToday } from "./FoodTruck";

const TRUCK_SCHEDULE = gql`${truckSchedule}`;

const TRUCK_ORANGE = "#E36A1A";
const DEAL_INK = "#A3470C";
const DEAL_BG = "#FFF1E6";
const ROW_LINE = "#F0EEE9";
const STOP_BORDER = "#F3C9C4";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
  70% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
`;

// "2026-11-09" → { y, m, d, dow }
const partsOf = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d, dow: new Date(y, m - 1, d).getDay() };
};
const shortDay = (iso) => {
  const { y, m, d } = partsOf(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const longDay = (iso) => {
  const { m, d } = partsOf(iso);
  return `${MONTHS[m - 1]} ${d}`;
};
const endOf = (windowLabel) => (windowLabel || "").split("–").pop().trim();
const startOf = (windowLabel) => (windowLabel || "").split("–")[0].trim();
const spotName = (stop) => titleCaseSpot(stop?.label) || shortAddress(stop?.address) || "";
const dealsLabel = (n) => `${n} ${n === 1 ? "deal" : "deals"}`;
const sameText = (a, b) => String(a).replace(/\s+/g, " ").trim().toLowerCase() === String(b).replace(/\s+/g, " ").trim().toLowerCase();

function directionsUrl(stop) {
  const lat = Number(stop?.latitude);
  const lng = Number(stop?.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng)) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  return stop?.address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}` : null;
}

// Every deal once, whichever days it runs on.
const uniqueDeals = (days) => {
  const seen = new Map();
  (days || []).forEach((d) => (d.deals || []).forEach((x) => seen.set(String(x._id), x)));
  return [...seen.values()];
};

const chipBase = {
  display: "inline-flex", alignItems: "center", gap: 0.75, height: 32, px: 1.5, borderRadius: RADIUS.pill,
  fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", listStyle: "none",
};
const outlineButton = {
  ...BUTTON_RESET, ...FOCUS_RING,
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.75, height: 54, px: 2.5,
  borderRadius: RADIUS.controlLg, border: `1px solid ${C.borderStrong}`, color: C.ink, bgcolor: C.bg,
  fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap",
  "&:hover": { bgcolor: C.surface },
};

/* ------------------------------------------------------------- deals popup */

function CopyCode({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => {
        try { navigator.clipboard?.writeText(code); } catch (e) { /* the code is on the pill to read */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      aria-label={copied ? `Code ${code} copied` : `Copy code ${code}`}
      sx={{
        ...BUTTON_RESET, ...FOCUS_RING, display: "inline-flex", alignItems: "center", gap: 0.75, height: 34, px: 1.5,
        borderRadius: RADIUS.pill, border: `1.5px dashed ${DEAL_INK}`, color: DEAL_INK, bgcolor: DEAL_BG,
        fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em",
      }}
    >
      {code}
      {copied ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />}
    </Box>
  );
}

function DealsDialog({ open, title, deals, onClose }) {
  return (
    <RmfDialog open={open} onClose={onClose} maxWidth={480}>
      <DialogTitle>{title}</DialogTitle>
      <Box component="ul" sx={{ m: 0, mt: 2, p: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {deals.map((d) => (
          <Box component="li" key={d._id} sx={{ listStyle: "none", p: 2, borderRadius: RADIUS.menuCard, bgcolor: DEAL_BG }}>
            <Box sx={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 800, color: C.ink }}>{d.name}</Box>
            {d.description && !sameText(d.description, d.name) && <Box sx={{ ...TYPE.body, color: C.muted, mt: 0.25 }}>{d.description}</Box>}
            <Box sx={{ mt: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {d.couponCode ? <CopyCode code={d.couponCode} /> : null}
              <Box component="span" sx={{ ...TYPE.meta, color: C.muted }}>Apply at checkout</Box>
            </Box>
          </Box>
        ))}
      </Box>
      <DialogActions>
        <Box component="button" type="button" onClick={onClose} sx={dialogButtonSx.secondary}>Close</Box>
      </DialogActions>
    </RmfDialog>
  );
}

/* ------------------------------------------------------------------ header */

function TruckHeader({ restaurant, truck, dealCount, onDeals }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = !photoFailed ? truck.imageUrl || restaurant?.image : restaurant?.image;
  const open = !!truck.isOpenNow;
  const where = open ? truck.currentStop : truck.nextStop || truck.currentStop;
  const status = open
    ? `Open now · until ${endOf(truck.currentStop?.windowLabel)}`
    : (() => {
        const when = (truck.statusLabel || "").split(" · ")[0];
        return /^Back /.test(when) ? `Closed · back ${when.slice(5)}` : "Closed";
      })();

  return (
    <Box
      component="header"
      sx={{
        position: "relative", mt: 2, minHeight: 220, borderRadius: "26px", overflow: "hidden", bgcolor: C.ink,
        display: "flex", flexDirection: { xs: "column", md: "row" },
      }}
    >
      {photo && (
        <Box
          component="img"
          src={resizedImage(photo, 1000)}
          alt={`Food from ${truck.name}`}
          onError={() => setPhotoFailed(true)}
          sx={{
            position: { xs: "relative", md: "absolute" }, top: 0, right: 0, bottom: 0,
            width: { xs: "100%", md: "48%" }, height: { xs: 160, md: "100%" }, objectFit: "cover", display: "block",
          }}
        />
      )}
      <Box
        sx={{
          position: "relative", zIndex: 1, bgcolor: C.ink, width: { xs: "100%", md: "56%" }, boxSizing: "border-box",
          minHeight: { md: 220 }, p: { xs: 2.5, md: "28px 36px" }, mt: { xs: photo ? "-24px" : 0, md: 0 },
          borderRadius: { xs: "24px 24px 0 0", md: "0 40px 40px 0" },
          display: "flex", flexDirection: "column", justifyContent: "center", gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: "14px", bgcolor: C.goldAccent, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 28, color: C.ink }} />
          </Box>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, color: "#D9D6CF", overflowWrap: "anywhere" }}>{restaurant?.name}</Box>
        </Box>
        <Box component="h1" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 34, md: 44 }, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#FFFFFF", overflowWrap: "anywhere" }}>
          {truck.name}
        </Box>
        <Box component="ul" aria-label="Truck status" sx={{ display: "flex", flexWrap: "wrap", gap: 1, m: 0, p: 0 }}>
          <Box component="li" sx={{ ...chipBase, bgcolor: open ? C.greenSoft : C.goldSoft, color: open ? C.green : C.gold }}>
            {open && <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.green }} />}
            {status}
          </Box>
          {where?.scheduleLabel && (
            <Box component="li" sx={{ ...chipBase, bgcolor: "rgba(255,255,255,0.12)", color: "#FFFFFF", fontWeight: 600 }}>
              {`${where.scheduleLabel} · ${spotName(where)}`}
            </Box>
          )}
          {dealCount > 0 && (
            <Box component="li" sx={{ listStyle: "none" }}>
              <Box
                component="button"
                type="button"
                onClick={onDeals}
                sx={{ ...BUTTON_RESET, ...chipBase, bgcolor: TRUCK_ORANGE, color: "#FFFFFF", "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" } }}
              >
                <LocalOfferOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                {dealsLabel(dealCount)}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------------------- today card */

function TodayCard({ day, onOrder, onDeals, cardRef }) {
  const { stop } = day;
  const servingNow = !!day.isOpenNow;
  const directions = directionsUrl(stop);
  const notYet = !servingNow && day.canOrder;
  const statusText = notYet ? `Opens today at ${startOf(stop.windowLabel)}` : "Finished for today";
  return (
    <Box
      component="section"
      ref={cardRef}
      aria-labelledby="today-title"
      tabIndex={-1}
      sx={{
        border: `2px solid ${C.red}`, borderRadius: "24px", overflow: "hidden", bgcolor: C.bg,
        boxShadow: "0 12px 32px rgba(196,43,31,0.14)", scrollMarginTop: topBarOffset(16), outline: "none",
      }}
    >
      <Box sx={{ bgcolor: C.red, color: "#FFFFFF", px: { xs: 2.5, md: 3 }, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box component="h2" id="today-title" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {`Today · ${shortDay(day.date)}`}
        </Box>
        {servingNow && (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700 }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#FFFFFF", animation: `${pulse} 1.8s ease-out infinite`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
            />
            Serving now
          </Box>
        )}
      </Box>
      <Box sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { md: "center" }, gap: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted }}>Parked at</Box>
          <Box component="p" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.015em", color: C.ink, overflowWrap: "anywhere" }}>
            {spotName(stop)}
          </Box>
          {stop.label && stop.address && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, ...TYPE.body, color: C.muted }}>
              <PlaceOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, mt: "1px" }} />
              <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{shortAddress(stop.address)}</Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 1, borderRadius: "14px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>
              <AccessTimeRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
              {stop.windowLabel}
            </Box>
            {day.deals?.length > 0 && (
              <Box
                component="button"
                type="button"
                onClick={onDeals}
                sx={{ ...BUTTON_RESET, ...FOCUS_RING, display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 1, borderRadius: "14px", bgcolor: DEAL_BG, color: DEAL_INK, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}
              >
                <LocalOfferOutlinedIcon aria-hidden="true" sx={{ fontSize: 17 }} />
                {`${dealsLabel(day.deals.length)} today`}
                <ChevronRightRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row", md: "column" }, gap: 1, flexShrink: 0, width: { xs: "100%", md: 240 } }}>
          {servingNow ? (
            <Box
              component="button"
              type="button"
              onClick={() => onOrder(day)}
              sx={{ ...BUTTON_RESET, ...FOCUS_RING, height: 54, px: 3, borderRadius: RADIUS.controlLg, bgcolor: C.red, color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap", flex: { sm: 1, md: "none" }, "&:hover": { bgcolor: C.redHover } }}
            >
              Order for pickup →
            </Box>
          ) : (
            <Box role="status" sx={{ height: 54, px: 2, borderRadius: RADIUS.controlLg, bgcolor: C.surface, color: C.ink, fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", flex: { sm: 1, md: "none" } }}>
              {statusText}
            </Box>
          )}
          {directions && (
            <Box component="a" href={directions} target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${spotName(stop)} (opens maps)`} sx={{ ...outlineButton, flex: { sm: 1, md: "none" } }}>
              <DirectionsOutlinedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
              Directions
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ----------------------------------------------------------- upcoming stops */

function DateBlock({ iso, selected }) {
  const { d, dow } = partsOf(iso);
  return (
    <Box aria-hidden="true" sx={{ width: 64, flexShrink: 0, borderRadius: "14px", overflow: "hidden", border: `1px solid ${selected ? C.red : C.border}`, textAlign: "center", bgcolor: selected ? C.red : C.bg }}>
      <Box sx={{ bgcolor: selected ? C.redDark : C.ink, color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", py: 0.5 }}>{DOW[dow]}</Box>
      <Box sx={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 800, lineHeight: 1, py: 1, color: selected ? "#FFFFFF" : C.ink }}>{d}</Box>
    </Box>
  );
}

function StopRow({ day, selected, onSelect, rowRef }) {
  const { stop } = day;
  const details = [stop.label ? shortAddress(stop.address) : null, stop.windowLabel].filter(Boolean).join(" · ");
  const n = day.deals?.length || 0;
  const spoken = [shortDay(day.date), spotName(stop), details, n ? dealsLabel(n) : null, "ordering opens that day"].filter(Boolean).join(", ");
  return (
    <Box component="li" ref={rowRef} sx={{ listStyle: "none", borderTop: `1px solid ${ROW_LINE}`, "&:first-of-type": { borderTop: 0 }, scrollMarginTop: topBarOffset(24), scrollMarginBottom: "24px" }}>
      {/* Phones put the deals and lock under the text; wider screens on the right. */}
      <Box
        component="button"
        type="button"
        onClick={() => onSelect(day)}
        aria-pressed={selected}
        aria-label={spoken}
        sx={{
          ...BUTTON_RESET, width: "100%", p: { xs: 1.75, md: 2 }, boxSizing: "border-box", textAlign: "left",
          display: "grid", alignItems: "center", columnGap: 2, rowGap: 1,
          gridTemplateColumns: { xs: "64px minmax(0, 1fr)", sm: "64px minmax(0, 1fr) auto" },
          bgcolor: selected ? C.redTint : C.bg, transition: "background-color .15s ease",
          "&:hover": { bgcolor: selected ? C.redTint : C.surface },
          "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "-3px" },
        }}
      >
        <Box sx={{ gridRow: { xs: "span 2", sm: "auto" } }}>
          <DateBlock iso={day.date} selected={selected} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 16, fontWeight: 700, color: C.ink, overflowWrap: "anywhere" }}>{spotName(stop)}</Box>
          <Box sx={{ ...TYPE.meta, color: C.muted, mt: 0.25, overflowWrap: "anywhere" }}>{details}</Box>
          {stop.ratingCount > 0 && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, mt: 0.5, ...TYPE.meta, color: C.ink, fontWeight: 700 }}>
              <StarRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: C.goldAccent }} />
              {Number(stop.rating).toFixed(1)}
              <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>{`(${stop.ratingCount})`}</Box>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", flexDirection: { xs: "row", sm: "column" }, alignItems: { xs: "center", sm: "flex-end" }, gap: { xs: 1, sm: 0.75 } }}>
          <RowFacts day={day} />
        </Box>
      </Box>
    </Box>
  );
}

function RowFacts({ day }) {
  const n = day.deals?.length || 0;
  return (
    <>
      {n > 0 && (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, height: 28, px: 1.25, borderRadius: RADIUS.pill, bgcolor: DEAL_BG, color: DEAL_INK, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
          <LocalOfferOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
          {dealsLabel(n)}
        </Box>
      )}
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ...TYPE.meta, fontSize: 12, fontWeight: 600, color: C.muted, whiteSpace: "nowrap" }}>
        <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
        {`Opens ${shortDay(day.date)}`}
      </Box>
    </>
  );
}

/* ----------------------------------------------------------------- calendar */

function MonthCalendar({ month, monthLabel, today, stopDates, selectedDate, canGoBack, onPrev, onNext, onPick }) {
  const [y, m] = month.split("-").map(Number);
  const firstDow = new Date(y, m - 1, 1).getDay();
  const daysIn = new Date(y, m, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  const iso = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const roundNav = (disabled) => ({
    ...BUTTON_RESET, ...FOCUS_RING, width: 40, height: 40, borderRadius: "50%", border: `1px solid ${C.borderStrong}`,
    display: "inline-flex", alignItems: "center", justifyContent: "center", color: C.ink, bgcolor: C.bg,
    opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer", "&:hover": { bgcolor: disabled ? C.bg : C.surface },
  });

  return (
    <Box
      component="aside"
      aria-label="Month calendar"
      sx={{ position: { md: "sticky" }, top: { md: topBarOffset(16) }, width: "100%", maxWidth: { md: 380 }, boxSizing: "border-box", p: 2.5, borderRadius: "22px", border: `1px solid ${C.border}`, bgcolor: C.bg }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box component="button" type="button" onClick={onPrev} disabled={!canGoBack} aria-label="Previous month" sx={roundNav(!canGoBack)}>
          <ChevronLeftRoundedIcon />
        </Box>
        <Box component="h2" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 800, color: C.ink }}>{monthLabel || `${MONTHS[m - 1]} ${y}`}</Box>
        <Box component="button" type="button" onClick={onNext} aria-label="Next month" sx={roundNav(false)}>
          <ChevronRightRoundedIcon />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 0.75, justifyItems: "center" }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((h, i) => (
          <Box key={`h${i}`} aria-hidden="true" sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, color: C.muted, pb: 0.5 }}>{h}</Box>
        ))}
        {cells.map((d, i) => {
          if (!d) return <Box key={`b${i}`} aria-hidden="true" />;
          const date = iso(d);
          const isToday = date === today;
          const isStop = stopDates.has(date) && date >= today;
          const base = { width: 40, height: 40, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_BODY, fontSize: 14, boxSizing: "border-box" };
          if (!isStop) {
            return (
              <Box key={date} sx={{ ...base, color: isToday ? "#FFFFFF" : C.muted, bgcolor: isToday ? C.red : "transparent", fontWeight: isToday ? 800 : 500 }}>
                {d}
              </Box>
            );
          }
          const selected = date === selectedDate;
          return (
            <Box
              key={date}
              component="button"
              type="button"
              onClick={() => onPick(date)}
              aria-pressed={selected}
              aria-label={`${longDay(date)}${isToday ? ", today" : ""}, truck stop`}
              sx={{
                ...BUTTON_RESET, ...base, fontWeight: 800,
                bgcolor: isToday ? C.red : C.redTint,
                color: isToday ? "#FFFFFF" : C.redDark,
                border: `1px solid ${isToday ? C.red : STOP_BORDER}`,
                boxShadow: selected && !isToday ? `0 0 0 2px ${C.red}` : "none",
                "&:hover": { bgcolor: isToday ? C.redHover : C.redSoft },
                "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "2px" },
              }}
            >
              {d}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 2, ...TYPE.meta, fontSize: 12, color: C.muted }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box aria-hidden="true" sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: C.red }} /> Today
        </Box>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box aria-hidden="true" sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: C.redTint, border: `1px solid ${STOP_BORDER}` }} /> Truck stop
        </Box>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------------------------- page */

export default function TruckSchedulePage({ restaurant, truckId, month, onMonth, onBack, onOrderStop }) {
  const today = localToday();
  const thisMonth = today.slice(0, 7);
  const shownMonth = /^\d{4}-\d{2}$/.test(month || "") && month >= thisMonth ? month : thisMonth;

  // Today's card always comes from the current month; the list and calendar
  // follow the month being looked at. The same month is one request.
  const current = useQuery(TRUCK_SCHEDULE, { variables: { truckId, month: thisMonth }, skip: !truckId, fetchPolicy: "cache-and-network" });
  const shown = useQuery(TRUCK_SCHEDULE, { variables: { truckId, month: shownMonth }, skip: !truckId, fetchPolicy: "cache-and-network" });
  const sched = shown.data?.truckSchedule;
  const nowSched = current.data?.truckSchedule;
  const truck = (nowSched || sched)?.truck;

  const [selectedKey, setSelectedKey] = useState(null);
  const [dealsOpen, setDealsOpen] = useState(null); // { title, deals }
  const rowRefs = useRef({});
  const todayRef = useRef(null);

  const todays = (nowSched?.days || []).filter((d) => d.date === today);
  const todayDay = todays.find((d) => d.isOpenNow) || todays.find((d) => d.canOrder) || todays[todays.length - 1] || null;
  const upcoming = useMemo(() => (sched?.days || []).filter((d) => d.date > today), [sched, today]);
  const stopDates = useMemo(() => new Set((sched?.days || []).map((d) => d.date)), [sched]);
  const allDeals = useMemo(() => uniqueDeals([...(nowSched?.days || []), ...(sched?.days || [])]), [nowSched, sched]);
  const keyOf = (d) => `${d.date}|${d.stop._id}`;
  const selectedDate = selectedKey ? selectedKey.split("|")[0] : null;

  const pickDate = (date) => {
    if (date === today && todayRef.current) {
      setSelectedKey(null);
      todayRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      todayRef.current.focus({ preventScroll: true });
      return;
    }
    const day = upcoming.find((d) => d.date === date);
    if (!day) return;
    setSelectedKey(keyOf(day));
    rowRefs.current[keyOf(day)]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const loading = (shown.loading && !shown.data) || (current.loading && !current.data);
  const error = shown.error || current.error;

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 }, pt: topBarOffset(16), pb: 8 }}>
      <Box
        component="button"
        type="button"
        onClick={onBack}
        sx={{ ...BUTTON_RESET, ...FOCUS_RING, display: "inline-flex", alignItems: "center", gap: 0.25, minHeight: 36, borderRadius: "8px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.redLink, "&:hover": { textDecoration: "underline" } }}
      >
        <ChevronLeftRoundedIcon aria-hidden="true" sx={{ fontSize: 20, ml: -0.5 }} />
        All trucks
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: C.red }} /></Box>
      ) : error || !truck ? (
        <Box sx={{ mt: 2, p: 4, borderRadius: "22px", border: `1px dashed ${C.borderStrong}`, textAlign: "center", ...TYPE.body, color: C.muted }}>
          We couldn't load this truck's schedule. Please refresh the page.
        </Box>
      ) : (
        <>
          <TruckHeader
            restaurant={restaurant}
            truck={truck}
            dealCount={allDeals.length}
            onDeals={() => setDealsOpen({ title: `Deals at ${truck.name}`, deals: allDeals })}
          />

          <Box
            sx={{
              mt: "36px", display: "grid", gap: { xs: 3, md: 4 }, alignItems: "start",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(300px, 380px)" },
            }}
          >
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "36px" }}>
              {todayDay && (
                <TodayCard
                  day={todayDay}
                  cardRef={todayRef}
                  onOrder={onOrderStop}
                  onDeals={() => setDealsOpen({ title: "Deals today", deals: todayDay.deals || [] })}
                />
              )}

              <Box component="section" aria-labelledby="upcoming-title">
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                  <Box component="h2" id="upcoming-title" sx={{ ...TYPE.sectionTitle, fontSize: { xs: 22, md: 24 }, fontWeight: 800, m: 0, color: C.ink }}>
                    Upcoming stops
                  </Box>
                  <Box component="p" sx={{ ...TYPE.meta, m: 0, color: C.muted }}>Ordering opens on the day of each stop</Box>
                </Box>
                {upcoming.length ? (
                  <Box component="ol" sx={{ m: 0, p: 0, border: `1px solid ${C.border}`, borderRadius: "22px", overflow: "hidden" }}>
                    {upcoming.map((d) => (
                      <StopRow
                        key={keyOf(d)}
                        day={d}
                        selected={selectedKey === keyOf(d)}
                        onSelect={(day) => setSelectedKey(keyOf(day))}
                        rowRef={(el) => { rowRefs.current[keyOf(d)] = el; }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 3, borderRadius: "22px", border: `1px dashed ${C.borderStrong}`, textAlign: "center", ...TYPE.body, color: C.muted }}>
                    {`No more stops in ${sched?.monthLabel || "this month"}.`}
                    {sched?.nextMonth && (
                      <Box component="button" type="button" onClick={() => onMonth(sched.nextMonth)} sx={{ ...BUTTON_RESET, ...FOCUS_RING, display: "block", mx: "auto", mt: 1, borderRadius: "8px", fontWeight: 700, color: C.redLink }}>
                        See next month
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <MonthCalendar
              month={shownMonth}
              monthLabel={sched?.monthLabel}
              today={today}
              stopDates={stopDates}
              selectedDate={selectedDate}
              canGoBack={shownMonth > thisMonth}
              onPrev={() => sched?.prevMonth && onMonth(sched.prevMonth)}
              onNext={() => sched?.nextMonth && onMonth(sched.nextMonth)}
              onPick={pickDate}
            />
          </Box>
        </>
      )}

      <DealsDialog open={!!dealsOpen} title={dealsOpen?.title || ""} deals={dealsOpen?.deals || []} onClose={() => setDealsOpen(null)} />
    </Box>
  );
}
